<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ListingDiscoveryCard from './ListingDiscoveryCard.vue';
import MapDrawArea from '../MapDrawArea.vue';

const props = defineProps({
  cards: { type: Array, default: () => [] },
  activeId: { type: [String, Number], default: '' },
  showMap: { type: Boolean, default: false },
});

const emit = defineEmits([
  'open',
  'hover',
  'toggle-select',
  'public-link',
  'share',
  'share-error',
  'viewed',
  'mapSelect',
]);

const { t } = useI18n();

const markers = computed(() =>
  props.cards
    .filter(card => card.latitude != null && card.longitude != null)
    .map(card => ({
      id: card.id,
      lat: card.latitude,
      lng: card.longitude,
      title: card.title,
      card,
    }))
);

const center = computed(() => {
  const active =
    markers.value.find(
      marker => String(marker.id) === String(props.activeId)
    ) || markers.value[0];
  return active ? { lat: active.lat, lng: active.lng } : undefined;
});

const activeCard = computed(() =>
  props.cards.find(card => String(card.id) === String(props.activeId))
);

const markerCountLabel = computed(() =>
  t('PROPERTIES.LIST.MAP.MARKER_COUNT', {
    shown: markers.value.length,
    total: props.cards.length,
  })
);

const markerLabel = marker =>
  String(marker.id) === String(props.activeId)
    ? t('PROPERTIES.LIST.MAP.SELECTED_MARKER', { title: marker.title })
    : marker.title;

const markerPosition = marker => marker?.card?.position || marker?.id;

const handleMapMarkerClick = marker => {
  emit('mapSelect', marker.card || marker);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="hidden min-h-0 gap-4 md:grid"
    :class="
      showMap
        ? 'lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,1.05fr)]'
        : 'grid-cols-1'
    "
  >
    <div class="grid min-w-0 gap-3">
      <ListingDiscoveryCard
        v-for="card in cards"
        :key="card.id"
        :card="card"
        :active="String(card.id) === String(activeId)"
        compact
        @open="payload => emit('open', payload)"
        @hover="payload => emit('hover', payload)"
        @toggle-select="payload => emit('toggle-select', payload)"
        @public-link="payload => emit('public-link', payload)"
        @share="
          (cardPayload, sharePayload) =>
            emit('share', cardPayload, sharePayload)
        "
        @share-error="
          (cardPayload, error) => emit('share-error', cardPayload, error)
        "
        @viewed="payload => emit('viewed', payload)"
      />
    </div>

    <aside v-if="showMap" class="sticky top-3 hidden self-start lg:block">
      <div
        class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
      >
        <div
          class="flex items-center justify-between gap-2 border-b border-lais-border-soft px-3 py-2"
        >
          <p class="text-[12px] font-semibold text-lais-text">
            {{ t('PROPERTIES.LIST.MAP.TITLE') }}
          </p>
          <span
            class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
          >
            {{ markerCountLabel }}
          </span>
        </div>
        <MapDrawArea
          v-if="markers.length && center"
          :markers="markers"
          :center="center"
          :enable-draw="false"
          @marker-click="handleMapMarkerClick"
        />
        <div
          v-else
          class="flex h-[420px] flex-col items-center justify-center gap-2 bg-lais-bg-muted px-6 text-center text-lais-text-muted"
        >
          <span class="i-lucide-map-pinned size-8" aria-hidden="true" />
          <p class="text-[13px] font-semibold">
            {{ t('PROPERTIES.LIST.MAP.NO_COORDINATES') }}
          </p>
        </div>

        <div
          class="border-t border-lais-border-soft bg-white p-3"
          aria-live="polite"
        >
          <p class="text-[12px] font-semibold text-lais-text">
            {{ activeCard?.title || t('PROPERTIES.LIST.MAP.HOVER_HINT') }}
          </p>
          <div class="mt-2 flex flex-wrap gap-1.5">
            <button
              v-for="marker in markers.slice(0, 20)"
              :key="marker.id"
              type="button"
              class="inline-flex size-7 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors"
              :class="
                String(marker.id) === String(activeId)
                  ? 'border-lais-primary bg-lais-primary text-white'
                  : 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
              "
              :aria-label="markerLabel(marker)"
              @click="handleMapMarkerClick(marker)"
            >
              {{ markerPosition(marker) }}
            </button>
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>
